import { reactive } from "vue";
import type {
  AddressResolvedPayload,
  AddressSelectionState,
  AddressSelectorProvider,
  AddressSelectorQueryInput,
} from "../types";

/** コア初期化オプションを表す型。 */
type UseAddressSelectorCoreOptions = {
  otherLabels?: string[];
  initialState?: Partial<AddressSelectionState>;
};

/** 住所選択コアに必要な依存をまとめた型。 */
interface UseAddressSelectorCoreParams<TContext> {
  provider: AddressSelectorProvider<TContext>;
  context?: TContext;
  options?: UseAddressSelectorCoreOptions;
}

const DEFAULT_OTHER_LABELS = ["その他", "そのほか", "他"];

/**
 * 住所選択状態の初期値を生成する。
 */
const createInitialState = (): AddressSelectionState => ({
  zipCode: "",
  pref: "",
  city: "",
  section: "",
  block: "",
  cityBlockList: null,
  sectionBlockList: null,
  blockList: [],
  errors: {
    zip: "",
    pref: "",
    general: "",
  },
  dialog: {
    addressDialog: false,
    areaDialog: false,
  },
});

/**
 * 「その他」表記を正規化する。
 */
const normalizeOtherLabel = (value: string, candidates: string[]): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  return candidates.includes(trimmed) ? "その他" : trimmed;
};

/**
 * 住所選択UIの共通状態と遷移を扱う composable。
 */
export const useAddressSelectorCore = <TContext = unknown>({
  provider,
  context,
  options,
}: UseAddressSelectorCoreParams<TContext>) => {
  const state = reactive<AddressSelectionState>({
    ...createInitialState(),
    ...(options?.initialState ?? {}),
    errors: {
      ...createInitialState().errors,
      ...(options?.initialState?.errors ?? {}),
    },
    dialog: {
      ...createInitialState().dialog,
      ...(options?.initialState?.dialog ?? {}),
    },
  });

  const otherLabels = options?.otherLabels ?? DEFAULT_OTHER_LABELS;

  /**
   * 共通エラー表示を初期化する。
   */
  const clearErrors = (): void => {
    state.errors.zip = "";
    state.errors.pref = "";
    state.errors.general = "";
  };

  /**
   * エリア選択状態を初期化する。
   */
  const clearAreaState = (): void => {
    state.city = "";
    state.section = "";
    state.block = "";
    state.cityBlockList = null;
    state.sectionBlockList = null;
    state.blockList = [];
  };

  /**
   * ダイアログ表示を制御する。
   */
  const setDialog = (addressDialog: boolean, areaDialog: boolean): void => {
    state.dialog.addressDialog = addressDialog;
    state.dialog.areaDialog = areaDialog;
  };

  /**
   * 郵便番号入力を検証する。
   */
  const validateZip = (): boolean => {
    state.errors.zip = "";
    const message = provider.validateZip(state.zipCode);
    if (message) {
      state.errors.zip = message;
      return false;
    }
    return true;
  };

  /**
   * 郵便番号モードへ切り替える。
   */
  const switchToZipMode = (): void => {
    clearErrors();
    clearAreaState();
  };

  /**
   * 都道府県モードへ切り替える。
   */
  const switchToPrefMode = (): void => {
    clearErrors();
    clearAreaState();
    state.zipCode = "";
  };

  /**
   * 郵便番号から住所候補を取得する。
   */
  const searchByZip = async (): Promise<AddressResolvedPayload | null> => {
    clearErrors();
    clearAreaState();

    if (!validateZip()) {
      return null;
    }

    const result = await provider.searchByZip(
      {
        zipCode: state.zipCode,
      },
      context
    );

    state.cityBlockList = result.cityBlockList;
    state.sectionBlockList = result.sectionBlockList;
    state.blockList = result.blockList;
    state.pref = result.selectedPref ?? state.pref;
    state.city = result.selectedCity ?? state.city;
    state.section = result.selectedSection ?? state.section;

    if (result.resolved) {
      await provider.applyResolvedAddress(result.resolved, context);
      setDialog(false, false);
      return result.resolved;
    }

    const hasCandidates =
      result.blockList.length > 0 ||
      result.cityBlockList !== null ||
      result.sectionBlockList !== null;

    if (hasCandidates) {
      setDialog(false, true);
    } else {
      setDialog(true, false);
    }

    return null;
  };

  /**
   * 都道府県選択から市区候補を取得する。
   */
  const loadCityListByPref = async (): Promise<void> => {
    clearErrors();
    clearAreaState();

    if (!state.pref) {
      state.errors.pref = "都道府県が入力されていません。";
      return;
    }

    state.cityBlockList = await provider.getCityListByPref(
      {
        pref: state.pref,
      },
      context
    );

    setDialog(false, true);
  };

  /**
   * 市区町村選択から町域候補を取得する。
   */
  const selectCity = async (city: string): Promise<void> => {
    state.city = city;
    state.section = "";
    state.block = "";
    state.blockList = [];

    state.sectionBlockList = await provider.getSectionListByPrefCity(
      {
        pref: state.pref,
        city,
      },
      context
    );
  };

  /**
   * 町域選択から丁目候補または郵便番号を解決する。
   */
  const selectSection = async (section: string): Promise<AddressResolvedPayload | null> => {
    state.section = section;
    state.block = "";

    const blocks = await provider.getBlockListByPrefCitySection(
      {
        pref: state.pref,
        city: state.city,
        section,
      },
      context
    );

    state.blockList = blocks;

    if (blocks.length > 0) {
      return null;
    }

    const resolved = await provider.resolveZipByPrefCitySection(
      {
        pref: state.pref,
        city: state.city,
        section,
      },
      context
    );

    if (!resolved) {
      return null;
    }

    await provider.applyResolvedAddress(resolved, context);
    setDialog(false, false);
    return resolved;
  };

  /**
   * 丁目選択から住所を確定する。
   */
  const selectBlock = async (block: string): Promise<AddressResolvedPayload | null> => {
    const normalizedBlock = normalizeOtherLabel(block, otherLabels);
    state.block = normalizedBlock;

    const payload: AddressSelectorQueryInput = {
      pref: state.pref,
      city: state.city,
      section: state.section,
      block: normalizedBlock === "その他" ? "" : normalizedBlock,
    };

    const resolved = await provider.resolveZipByPrefCitySectionBlock(payload, context);

    if (!resolved) {
      return null;
    }

    await provider.applyResolvedAddress(resolved, context);
    setDialog(false, false);
    return resolved;
  };

  /**
   * エリアダイアログの戻る操作を処理する。
   */
  const backInAreaDialog = (): void => {
    if (state.blockList.length > 0) {
      state.block = "";
      state.blockList = [];
      return;
    }

    if (state.sectionBlockList) {
      state.section = "";
      state.sectionBlockList = null;
      // 市区町村候補がない場合（郵便番号検索で市区町村が確定済みの場合）は
      // エリア選択ダイアログを閉じて住所選択ダイアログへ戻る。
      if (!state.cityBlockList) {
        setDialog(true, false);
      }
      return;
    }

    if (state.cityBlockList) {
      state.city = "";
      state.cityBlockList = null;
    }

    setDialog(true, false);
  };

  /** 住所選択コアの公開API。 */
  return {
    state,
    clearErrors,
    clearAreaState,
    setDialog,
    switchToZipMode,
    switchToPrefMode,
    validateZip,
    searchByZip,
    loadCityListByPref,
    selectCity,
    selectSection,
    selectBlock,
    backInAreaDialog,
  };
};
